import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { BaseDialogComponent } from 'packages/client/src/app/components/kit/base-dialog/base-dialog.component';
import { SelectInputComponent } from 'packages/client/src/app/components/kit/input/select-input/select-input.component';
import { NumberInputComponent } from 'packages/client/src/app/components/kit/input/text-input/number-input.component';
import { DateInputComponent } from 'packages/client/src/app/components/kit/input/date-input/date-input.component';
import { TextAreaInputComponent } from 'packages/client/src/app/components/kit/input/text-area-input/text-area-input.component';
import { InputUserMultiselectComponent } from 'packages/client/src/app/modules/shared/components/inputs/input-user-multiselect/input-user-multiselect.component';
import { fb, gqlApi, routeParams } from 'packages/client/src/app/modules/shared/shared.global-service';
import { isInvalid } from 'packages/client/src/app/modules/shared/helpers/validate';
import { FolderWarehouseMaterialOperationDialogStore } from './warehouse-material-operation-dialog.store';
import { WarehouseMaterialOperationTypeEnum } from '@libs/types/src/services/enum/warehouse-material-operation.enum';
import { contentGlobalSuccess } from 'packages/client/src/app/modules/content/content.error';
import { WarehouseMaterialOperationObject } from '@libs/types/src/services/object-type/service/social-service/warehouse-material-operation.object';
import { getWarehouseMaterialsFrag } from '../warehouse-material.query';
import { InfiniteScrollStore } from 'packages/client/src/app/modules/shared/store/common/infinite-scroll.store';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { WarehouseMaterialStateEnum } from '@libs/types/src/services/enum/warehouse-material.enum';
import { BaseDialogService } from 'packages/client/src/app/components/kit/base-dialog/base-dialog-service';
import { WarehouseMaterialReplenishDialogComponent } from '../warehouse-material-replenish-dialog/warehouse-material-replenish-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { WarehouseMaterialObject } from '@libs/types/src/services/object-type/service/social-service/warehouse-material.object';
import {
    WarehouseMaterialOperationCreateInput,
} from '@libs/types/src/services/input-type/services/social-service/warehouse-material-operation.create-input';

type DeferredMaterialOperation = WarehouseMaterialOperationCreateInput & {
    localId?: number;
    warehouseMaterial: WarehouseMaterialObject | null;
};

@Component({
    selector: 'app-folder-warehouse-material-operation-dialog',
    templateUrl: 'warehouse-material-operation-dialog.component.html',
    providers: [FolderWarehouseMaterialOperationDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectInputComponent,
        NumberInputComponent,
        DateInputComponent,
        TextAreaInputComponent,
        InputUserMultiselectComponent,
    ],
})
export class FolderWarehouseMaterialOperationDialogComponent extends BaseDialogComponent {

    protected readonly store = inject(FolderWarehouseMaterialOperationDialogStore);
    protected readonly prefix = 'service.warehouseMaterialOperation.';
    protected readonly EmploymentParentGroupEnum = EmploymentParentGroupEnum;
    protected readonly serviceId = computed(() => this.dialogConfig.data.serviceId);

    private readonly dialogService = inject(BaseDialogService);
    private readonly translate = inject(TranslateService);

    protected readonly materialsScroll = computed(() => new InfiniteScrollStore(
        this.store.api.getWarehouseMaterials,
        FilterBuilder.singlePage({
            serviceId: this.serviceId() || 0,
            state: WarehouseMaterialStateEnum.ACTIVE,
        }),
        true,
        'id name code amountAvailable',
        undefined,
        true,
    ));

    protected form = fb.group({
        id: fb.control<number | null>(null),
        warehouseMaterialId: fb.control<number | null>(null, Validators.required),
        amountIssued: fb.control<number>(1),
        amountReturned: fb.control<number>(0),
        amountReceipt: fb.control<number>(0),
        amount: fb.control<number>(0),
        date: fb.control<Date | null>(new Date()),
        description: fb.control<string | null>(null),
        workerIds: fb.control<string[]>([]),
    });

    private folderId!: number;
    private item: WarehouseMaterialOperationObject | null = null;
    private readonly deferred = Boolean(this.dialogConfig.data.deferred); // Used for work creation where workId does not exists yet ... or can be cancelled
    protected readonly workOperation = Boolean(this.dialogConfig.data.workOperation);

    constructor() {
        super();
        this.folderId = this.dialogConfig.data.folderId;
        this.item = this.dialogConfig.data.item ?? null;

        this.form.patchValue({
            date: this.dialogConfig.data.date ? new Date(this.dialogConfig.data.date) : new Date(),
            workerIds: this.dialogConfig.data.workerIds || [],
        });

        if (this.item) {
            this.form.patchValue({
                id: this.item.id,
                warehouseMaterialId: this.item.warehouseMaterialId,
                amount: this.item.amount,
                date: this.item.date ? new Date(this.item.date) : null,
                description: this.item.description,
                workerIds: this.item.workerIds,
            });
        }

        effect(async () => {
            const userId = this.store.globalGlobalStore.loggedUserStore.currentUserId();

            if (!userId || this.form.controls.workerIds.value?.length) return;

            this.form.patchValue({ workerIds: [userId] });
        });
    }

    override save(_event: Event): void {
        if (isInvalid(this.form)) return;

        const amountIssued = this.form.controls.amountIssued.value || 0;
        const amountReturned = this.workOperation
            ? this.form.controls.amountReceipt.value || 0
            : this.form.controls.amountReturned.value || 0;
        const isEdit = !!this.item;

        if (!isEdit && amountIssued === 0 && amountReturned === 0) return;

        const baseData = {
            warehouseMaterialId: this.form.controls.warehouseMaterialId.value!,
            date: this.form.controls.date.value?.toISOString() ?? null,
            description: this.form.controls.description.value || null,
            folderId: this.folderId || null,
            workId: this.dialogConfig.data.workId
                ?? ((routeParams.workId() ? Number(routeParams.workId()) : null) || null),
            workerIds: this.form.controls.workerIds.value || [],
        };

        if (this.deferred) {
            this.dialogRef.close({
                operations: this.buildDeferredOperations(baseData, amountIssued, amountReturned, isEdit),
            });
            return;
        }

        this.executeOperations(baseData, amountIssued, amountReturned, isEdit);
    }

    private buildDeferredOperations(
        baseData: { warehouseMaterialId: number; date: string | null; description: string | null; folderId: number | null; workId: number | null; workerIds: string[] },
        amountIssued: number,
        amountReturned: number,
        isEdit: boolean,
    ): DeferredMaterialOperation[] {
        const warehouseMaterial = (this.materialsScroll().items() as WarehouseMaterialObject[])
            .find(material => material.id === baseData.warehouseMaterialId)
            || this.item?.warehouseMaterial
            || null;

        if (isEdit) {
            return [{
                ...baseData,
                localId: this.item!.id,
                type: this.item!.type,
                amount: this.form.controls.amount.value || 0,
                warehouseMaterial,
            }];
        }

        return [
            ...(amountIssued > 0 ? [{
                ...baseData,
                type: WarehouseMaterialOperationTypeEnum.ISSUED,
                amount: amountIssued,
                warehouseMaterial,
            }] : []),
            ...(amountReturned > 0 ? [{
                ...baseData,
                type: this.workOperation
                    ? WarehouseMaterialOperationTypeEnum.RECEIPT
                    : WarehouseMaterialOperationTypeEnum.RETURNED,
                amount: amountReturned,
                warehouseMaterial,
            }] : []),
        ];
    }

    private async executeOperations(
        baseData: { warehouseMaterialId: number; date: string | null; description: string | null; folderId: number | null; workId: number | null; workerIds: string[] },
        amountIssued: number,
        amountReturned: number,
        isEdit: boolean,
    ): Promise<void> {
        try {
            const operations: Promise<any>[] = [];

            if (isEdit) {
                operations.push(
                    gqlApi.patchWarehouseMaterialOperation({
                        id: this.item!.id,
                        type: this.item!.type,
                        amount: this.form!.controls.amount.value!,
                        date: baseData.date,
                        description: baseData.description,
                        folderId: baseData.folderId,
                        workerIds: baseData.workerIds,
                        warehouseMaterialId: baseData.warehouseMaterialId,
                    }, getWarehouseMaterialsFrag),
                );
            } else {
                if (amountIssued > 0) {
                    operations.push(
                        gqlApi.createWarehouseMaterialOperation({
                            ...baseData,
                            type: WarehouseMaterialOperationTypeEnum.ISSUED,
                            amount: amountIssued,
                        }, getWarehouseMaterialsFrag),
                    );
                }

                if (amountReturned > 0) {
                    operations.push(
                        gqlApi.createWarehouseMaterialOperation({
                            ...baseData,
                            type: this.workOperation
                                ? WarehouseMaterialOperationTypeEnum.RECEIPT
                                : WarehouseMaterialOperationTypeEnum.RETURNED,
                            amount: amountReturned,
                        }, getWarehouseMaterialsFrag),
                    );
                }
            }

            const results = await Promise.all(operations);
            if (results.some(r => !r)) return;

            contentGlobalSuccess.next(this.item ? 'common.updated' : 'common.created');
            this.dialogRef.close({ refresh: true });
        } catch (e: any) {
            const errorMessage = e?.graphQLErrors?.[0]?.message ?? '';
            if (errorMessage.includes('notEnoughMaterial')) {
                this.openReplenishDialog(baseData, amountIssued, amountReturned, isEdit);
            }
        }
    }

    private openReplenishDialog(
        baseData: { warehouseMaterialId: number; date: string | null; description: string | null; folderId: number | null; workId: number | null; workerIds: string[] },
        amountIssued: number,
        amountReturned: number,
        isEdit: boolean,
    ): void {
        const ref = this.dialogService.open(WarehouseMaterialReplenishDialogComponent, {
            header: this.translate.instant(this.prefix + 'replenishHeader'),
            styleClass: 'p-dialog-width-20',
            data: {
                warehouseMaterialId: baseData.warehouseMaterialId,
                date: baseData.date,
                description: baseData.description,
                folderId: baseData.folderId || null,
                workId: baseData.workId || null,
                workerIds: baseData.workerIds,
                missingAmount: this.findMissingAmount(),
            },
        });

        ref?.onClose?.subscribe((res) => {
            if (res?.retryOriginal) {
                this.executeOperations(baseData, amountIssued, amountReturned, isEdit);
            }
        });
    }

    private findMissingAmount(): number {
        const selectedMaterial = (this.materialsScroll().items() as WarehouseMaterialObject[])
            .find((m: WarehouseMaterialObject) => m.id === this.form.controls.warehouseMaterialId.value);
        const available = selectedMaterial?.amountAvailable ?? 0;
        const isEdit = !!this.item;
        const requested = isEdit
            ? (this.form.controls.amount.value || 0) - (this.item?.amount ?? 0)
            : this.form.controls.amountIssued.value || 0;

        return Math.max(requested - available, 0);
    }
}
